import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Save, RotateCcw, Info, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigation } from '@/components/layout/app-layout';
import { featureFlags, FeatureFlags } from '@/config/feature-flags';

const FEATURE_METADATA = {
  enableBatchLotTracking: {
    name: 'Batch/Lot Tracking',
    description: 'Track stock by batch or lot numbers with expiry dates',
    phase: 'Phase 2',
    impact: 'Adds batch/lot fields to stock movements and displays',
    dependencies: [],
  },
  enableSerialNumberTracking: {
    name: 'Serial Number Tracking',
    description: 'Track individual items by unique serial numbers',
    phase: 'Phase 2',
    impact: 'Adds serial number fields and individual item tracking',
    dependencies: [],
  },
  enableExpiryDateTracking: {
    name: 'Expiry Date Tracking',
    description: 'Track and manage expiry dates for perishable items',
    phase: 'Phase 2',
    impact: 'Adds expiry date fields and expiration warnings',
    dependencies: ['enableBatchLotTracking'],
  },
  enableStockReservations: {
    name: 'Stock Reservations',
    description: 'Reserve stock for orders and reduce available quantity',
    phase: 'Phase 2',
    impact: 'Shows reserved stock and calculates available quantity',
    dependencies: [],
  },
  enableLocationCapacityLimits: {
    name: 'Location Capacity Limits',
    description: 'Set and enforce maximum capacity limits for locations',
    phase: 'Phase 2',
    impact: 'Shows capacity usage and prevents over-capacity transfers',
    dependencies: [],
  },
  enableTransferApprovalWorkflow: {
    name: 'Transfer Approval Workflow',
    description: 'Require approval for stock transfers above certain thresholds',
    phase: 'Phase 3',
    impact: 'Adds approval workflow and notifications',
    dependencies: [],
  },
  enableScheduledTransfers: {
    name: 'Scheduled Transfers',
    description: 'Schedule stock transfers for future dates',
    phase: 'Phase 3',
    impact: 'Adds scheduling interface and automated execution',
    dependencies: [],
  },
  enableAdvancedReporting: {
    name: 'Advanced Reporting',
    description: 'Enable advanced analytics and custom reports',
    phase: 'Phase 3',
    impact: 'Adds advanced reporting dashboard and exports',
    dependencies: [],
  },
  enableStockValuation: {
    name: 'Stock Valuation',
    description: 'Calculate and display stock values and costs',
    phase: 'Phase 1',
    impact: 'Shows unit costs and total values in movements',
    dependencies: [],
  },
};

const PHASE_COLORS = {
  'Phase 1': 'bg-green-100 text-green-800 border-green-200',
  'Phase 2': 'bg-blue-100 text-blue-800 border-blue-200',
  'Phase 3': 'bg-purple-100 text-purple-800 border-purple-200',
};

export default function FeatureFlagsSettings() {
  const { navigateTo } = useNavigation();
  const [flags, setFlags] = useState<FeatureFlags>(featureFlags);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Load flags from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('stock-feature-flags');
    if (stored) {
      try {
        const storedFlags = JSON.parse(stored);
        setFlags(storedFlags);
      } catch (e) {
        console.error('Failed to parse stored feature flags:', e);
      }
    }
  }, []);

  const handleToggle = (key: keyof FeatureFlags) => {
    const newFlags = { ...flags, [key]: !flags[key] };
    
    // Check dependencies
    const metadata = FEATURE_METADATA[key];
    if (metadata.dependencies.length > 0 && newFlags[key]) {
      // If enabling a feature with dependencies, enable dependencies too
      metadata.dependencies.forEach((dep) => {
        newFlags[dep as keyof FeatureFlags] = true;
      });
    }
    
    // If disabling a feature, check if other features depend on it
    if (!newFlags[key]) {
      Object.entries(FEATURE_METADATA).forEach(([featureKey, featureMetadata]) => {
        if (featureMetadata.dependencies.includes(key)) {
          newFlags[featureKey as keyof FeatureFlags] = false;
        }
      });
    }
    
    setFlags(newFlags);
    setHasChanges(true);
    setShowSaveSuccess(false);
  };

  const handleSave = () => {
    localStorage.setItem('stock-feature-flags', JSON.stringify(flags));
    setHasChanges(false);
    setShowSaveSuccess(true);
    
    // Show toast notification
    toast.success('Feature flags saved successfully!', {
      description: 'Your changes have been applied.',
    });
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowSaveSuccess(false);
    }, 3000);
  };

  const handleReset = () => {
    setFlags(featureFlags);
    localStorage.removeItem('stock-feature-flags');
    setHasChanges(false);
    setShowSaveSuccess(false);
    
    // Show toast notification
    toast.info('Feature flags reset to defaults', {
      description: 'All custom settings have been cleared.',
    });
  };

  const groupedFeatures = {
    'Phase 1': Object.entries(FEATURE_METADATA).filter(([_, meta]) => meta.phase === 'Phase 1'),
    'Phase 2': Object.entries(FEATURE_METADATA).filter(([_, meta]) => meta.phase === 'Phase 2'),
    'Phase 3': Object.entries(FEATURE_METADATA).filter(([_, meta]) => meta.phase === 'Phase 3'),
  };

  const enabledCount = Object.values(flags).filter(Boolean).length;
  const totalCount = Object.keys(flags).length;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo('stock-options')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Feature Flags</h1>
            <p className="text-muted-foreground">
              Enable or disable features to customise your stock management system
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {enabledCount} of {totalCount} enabled
          </Badge>
        </div>
      </div>

      {/* Success Alert */}
      {showSaveSuccess && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Feature flags saved successfully! Changes will take effect immediately.
          </AlertDescription>
        </Alert>
      )}

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Feature flags allow you to enable or disable features without code changes. 
          Some features may have dependencies that will be automatically enabled/disabled.
        </AlertDescription>
      </Alert>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <span className="text-sm text-amber-800 flex-1">
            You have unsaved changes
          </span>
          <Button onClick={handleSave} size="sm">
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
          <Button onClick={handleReset} variant="outline" size="sm">
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
        </div>
      )}

      {/* Feature Groups */}
      {Object.entries(groupedFeatures).map(([phase, features]) => (
        <Card key={phase}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {phase}
                  <Badge className={PHASE_COLORS[phase as keyof typeof PHASE_COLORS]}>
                    {features.filter(([key]) => flags[key as keyof FeatureFlags]).length} / {features.length} enabled
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {phase === 'Phase 1' && 'Core features for basic stock management'}
                  {phase === 'Phase 2' && 'Advanced tracking and inventory management'}
                  {phase === 'Phase 3' && 'Workflow automation and advanced analytics'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {features.map(([key, metadata], index) => {
              const isEnabled = flags[key as keyof FeatureFlags];
              const hasDependencies = metadata.dependencies.length > 0;
              
              return (
                <div key={key}>
                  {index > 0 && <Separator className="my-4" />}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={key} className="text-base font-semibold cursor-pointer">
                          {metadata.name}
                        </Label>
                        {isEnabled && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Active
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {metadata.description}
                      </p>
                      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium">Impact:</span> {metadata.impact}
                        </div>
                        {hasDependencies && (
                          <div>
                            <span className="font-medium">Requires:</span>{' '}
                            {metadata.dependencies.map((dep) => 
                              FEATURE_METADATA[dep as keyof typeof FEATURE_METADATA]?.name
                            ).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                    <Switch
                      id={key}
                      checked={isEnabled}
                      onCheckedChange={() => handleToggle(key as keyof FeatureFlags)}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Bottom Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t shadow-lg">
          <div className="container mx-auto py-4 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              You have unsaved changes
            </span>
            <div className="flex gap-2">
              <Button onClick={handleReset} variant="outline">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
